import React, { useEffect, useMemo, useState } from 'react'

import { useLayerApiUrl } from './ImportDataContext'
import {
  createCustomTransactions,
  type CreateManualEntry,
  createMatchingCustomTransactionsFromManualEntries,
  type CustomAccount,
  getAccount,
  getManualEntriesForPayroll,
  ImportApiError,
  validateManualEntries,
} from './importDummyDataHelpers'
import { ImportForm } from './ImportForm'
import { type ImportStepDef } from './useImportForm'

export interface ImportPayrollManualEntriesProps {
  demoName: string
  businessId: string
  businessAccessToken: string
  defaultCustomAccount: CustomAccount
}

const buildPayrollManualEntrySteps = (
  businessId: string,
  businessAccessToken: string,
  customAccountId: string,
  layerApiUrl: string,
) => (manualEntries: CreateManualEntry[]): ImportStepDef[] => [
  ...manualEntries.map((entry, i) => ({
    label: `Creating manual entry ${i + 1}/${manualEntries.length}`,
    run: async () => {
      const manualEntriesResponse = await fetch(
        `${layerApiUrl}/v1/businesses/${businessId}/ledger/journal-entries`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${businessAccessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(entry),
        },
      )
      if (!manualEntriesResponse.ok) {
        throw new ImportApiError(
          `Manual entry creation failed: ${manualEntriesResponse.status} ${manualEntriesResponse.statusText}`,
          manualEntriesResponse.status,
        )
      }
    },
  })),
  {
    label: 'Creating matching transactions',
    run: async () => {
      const customTransactions = createMatchingCustomTransactionsFromManualEntries(
        businessId,
        manualEntries,
      )
      await createCustomTransactions(
        businessId,
        businessAccessToken,
        customAccountId,
        customTransactions,
        layerApiUrl,
      )
    },
  },
]

export const ImportPayrollManualEntries = ({
  demoName,
  businessId,
  businessAccessToken,
  defaultCustomAccount,
}: ImportPayrollManualEntriesProps) => {
  const layerApiUrl = useLayerApiUrl()
  const [customAccountLedgerAccountId, setCustomAccountLedgerAccountId] =
    useState<string | null>(null)
  useEffect(() => {
    const fetchAccount = async () => {
      const accountData = await getAccount(
        businessId,
        businessAccessToken,
        defaultCustomAccount.id,
        layerApiUrl,
      )
      setCustomAccountLedgerAccountId(accountData.ledger_account_id)
    }
    fetchAccount()
  }, [businessId, businessAccessToken, defaultCustomAccount.id, layerApiUrl])

  const getSteps = useMemo(
    () => buildPayrollManualEntrySteps(businessId, businessAccessToken, defaultCustomAccount.id, layerApiUrl),
    [businessId, businessAccessToken, defaultCustomAccount.id, layerApiUrl],
  )

  return customAccountLedgerAccountId ? (
    <ImportForm
      title='Payroll Manual Entries'
      defaultCustomAccount={defaultCustomAccount}
      getInitialData={() =>
        getManualEntriesForPayroll(customAccountLedgerAccountId, businessId)
      }
      validateData={validateManualEntries}
      getSteps={getSteps}
      dependencies={[demoName]}
    />
  ) : (
    <div>Loading...</div>
  )
}
